/**
 * Visual fan-out of markers that share one coordinate (stacked Muffen at a Kuppelpunkt,
 * a KVS on top of a sleeve, …).
 *
 * Marker 1 of a group stays exactly on the true coordinate; the others are shifted by a
 * constant number of PIXELS around it, applied as a CSS transform inside the icon. The
 * Leaflet marker itself keeps the true position, so the shift is the same on every zoom
 * level: the group always reads as one pile of touching markers. The former geographic
 * offset (0.00002° ≈ 2 m) was invisible at street zoom and hundreds of pixels at the
 * detail zoom, where marker 2 looked like it belonged somewhere else entirely (VER 2026-09-10).
 */

/** Ring radius in pixels — roughly one marker width, so the pile stays touching. */
export const CO_LOCATION_RING_PX = 18;

const RING_POSITIONS = 8;

/** Pixel shift of the marker with the given index in its overlap group (index 0 = true spot). */
export function coLocationPixelOffset(offsetIndex: number | undefined): { dx: number; dy: number } {
  if (offsetIndex === undefined || offsetIndex <= 0) {
    return { dx: 0, dy: 0 };
  }
  const angle = ((offsetIndex - 1) * Math.PI * 2) / RING_POSITIONS;
  return {
    dx: Math.round(Math.cos(angle) * CO_LOCATION_RING_PX),
    dy: Math.round(Math.sin(angle) * CO_LOCATION_RING_PX),
  };
}

/** Wraps a DivIcon's HTML so the icon is drawn shifted while the marker stays on the true coordinate. */
export function withCoLocationOffset(html: string, offsetIndex: number | undefined): string {
  const { dx, dy } = coLocationPixelOffset(offsetIndex);
  if (dx === 0 && dy === 0) {
    return html;
  }
  return `<div style="transform:translate(${dx}px,${dy}px)">${html}</div>`;
}

/** Tooltip offset that follows the shifted icon (base = the marker's normal tooltip offset). */
export function coLocationTooltipOffset(base: [number, number], offsetIndex: number | undefined): [number, number] {
  const { dx, dy } = coLocationPixelOffset(offsetIndex);
  return [base[0] + dx, base[1] + dy];
}

/**
 * Order of an overlap group: the element that stays on the true coordinate (index 0) should be
 * the one people look for — station or cabinet before house, house before sleeve. Without this
 * a neighbour zone's Kuppelpunkt-Muffe (placed on the foreign KVS by construction) took the true
 * spot on the planning map and the KVS itself was drawn displaced.
 */
const CO_LOCATION_RANK: Record<string, number> = {
  trafostation: 0,
  kabelverteilschrank: 1,
  hausanschluss: 2,
  muffe: 3,
};

export function sortOverlapGroup<T extends { type: string }>(group: T[]): T[] {
  if (group.length < 2) return group;
  return [...group].sort((a, b) => (CO_LOCATION_RANK[a.type] ?? 9) - (CO_LOCATION_RANK[b.type] ?? 9));
}
